/**
 * The plan view's line work, drawn from the measured model as SVG in hall metres: walls, the stage,
 * columns and side rooms, the divider, and dimension lines outside the walls. The overlay moves the
 * whole drawing with one transform, so it stays crisp at any zoom.
 */

import type { MeasuredModel, Reference, Vec2 } from '@mawkeb/shared/model';

export interface PlanLabel {
  /** Where the label sits, in hall metres. */
  at: Vec2;
  /** Screen pixels to move it by, away from the wall it labels. */
  nudge?: Vec2;
  /** A length to show in the current units, or fixed text. */
  metres?: number;
  text?: string;
  kind: 'dim' | 'note';
}

export interface PlanDrawing {
  svg: string;
  labels: PlanLabel[];
  /** Hall-metre box the drawing covers, dimension lines included. */
  extent: { min: Vec2; max: Vec2 };
}

const NS = 'http://www.w3.org/2000/svg';
export { NS as SVG_NS };

export interface PlanFit {
  /** px per metre */
  s: number;
  /** hall point at the middle of the screen */
  cx: number;
  cy: number;
}

/** The scale and centre that fit the drawing into the screen space the panels leave free. */
export function fitPlan(
  extent: { min: readonly number[]; max: readonly number[] },
  width: number,
  height: number,
  insets: { top: number; bottom: number; left: number; right: number },
): PlanFit {
  // room for the dimension labels that sit centred on lines outside the walls
  const pad = { x: Math.min(56, width * 0.1), y: 22 };
  const usableW = Math.max(100, width - insets.left - insets.right - pad.x * 2);
  const usableH = Math.max(100, height - insets.top - insets.bottom - pad.y * 2);
  const [x0, y0] = extent.min as [number, number];
  const [x1, y1] = extent.max as [number, number];
  const s = Math.min(usableW / (x1 - x0), usableH / (y1 - y0));
  const midX = (x0 + x1) / 2;
  const midY = (y0 + y1) / 2;
  return { s, cx: midX - (insets.left - insets.right) / 2 / s, cy: midY - (insets.bottom - insets.top) / 2 / s };
}

export function planMapping(fit: PlanFit, width: number, height: number) {
  return { s: fit.s, ox: width / 2 - fit.cx * fit.s, oy: height / 2 + fit.cy * fit.s };
}

const pts = (outline: readonly Vec2[]) => outline.map(([x, y]) => `${r(x)},${r(y)}`).join(' ');
const r = (v: number) => Math.round(v * 1000) / 1000;

function dimension(a: Vec2, b: Vec2, offset: Vec2, metres: number, labels: PlanLabel[], along = 0.5): string {
  const [ox, oy] = offset;
  const a2: Vec2 = [a[0] + ox, a[1] + oy];
  const b2: Vec2 = [b[0] + ox, b[1] + oy];
  const dx = b2[0] - a2[0];
  const dy = b2[1] - a2[1];
  const len = Math.hypot(dx, dy) || 1;
  const tx = (-dy / len) * 0.25;
  const ty = (dx / len) * 0.25;
  const olen = Math.hypot(ox, oy) || 1;
  labels.push({ at: [a2[0] + dx * along, a2[1] + dy * along], nudge: [(ox / olen) * 6, (-oy / olen) * 6], metres, kind: 'dim' });
  return [
    `<line class="dim-ext" x1="${r(a[0])}" y1="${r(a[1])}" x2="${r(a2[0] + ox * 0.15)}" y2="${r(a2[1] + oy * 0.15)}"/>`,
    `<line class="dim-ext" x1="${r(b[0])}" y1="${r(b[1])}" x2="${r(b2[0] + ox * 0.15)}" y2="${r(b2[1] + oy * 0.15)}"/>`,
    `<line class="dim" x1="${r(a2[0])}" y1="${r(a2[1])}" x2="${r(b2[0])}" y2="${r(b2[1])}"/>`,
    `<line class="dim" x1="${r(a2[0] - tx)}" y1="${r(a2[1] - ty)}" x2="${r(a2[0] + tx)}" y2="${r(a2[1] + ty)}"/>`,
    `<line class="dim" x1="${r(b2[0] - tx)}" y1="${r(b2[1] - ty)}" x2="${r(b2[0] + tx)}" y2="${r(b2[1] + ty)}"/>`,
  ].join('');
}

export function buildPlanDrawing(model: MeasuredModel): PlanDrawing {
  const labels: PlanLabel[] = [];
  const parts: string[] = [];
  const [w, l] = [model.bounds.max[0], model.bounds.max[1]];
  // corners of the hall can reach behind the line of the back wall
  const bottom = Math.min(0, model.bounds.min[1]);

  parts.push(`<polygon class="floor" points="${pts(model.floor.outline)}"/>`);
  for (const s of model.solids) {
    const cls = s.kind === 'stage' ? 'solid stage' : s.kind === 'column' ? 'solid column' : 'solid room';
    parts.push(`<polygon class="halo" points="${pts(s.outline)}"/><polygon class="${cls}" points="${pts(s.outline)}"/>`);
  }
  for (const line of model.lines) {
    const seg = `x1="${r(line.a[0])}" y1="${r(line.a[1])}" x2="${r(line.b[0])}" y2="${r(line.b[1])}"`;
    parts.push(`<line class="halo ${line.kind}-halo" ${seg}/><line class="${line.kind}" ${seg}/>`);
    if (line.kind === 'divider') labels.push({ at: [line.b[0] - 2.2, line.a[1] + 0.45], text: 'Divider', kind: 'note' });
  }
  const wallPath = model.walls.map((wall, i) => `${i === 0 ? 'M' : 'L'}${r(wall.a[0])},${r(wall.a[1])}`).join(' ') + ' Z';
  parts.push(`<path class="halo wall-halo" d="${wallPath}"/><path class="wall" d="${wallPath}"/>`);

  const ref = (id: string): Reference | undefined => model.references.find((x) => x.id === id);
  const front = ref('back_to_stage_front_centre');
  const length = ref('back_to_stage_back_wall');
  const stageWidth = ref('stage_width');
  const width = ref('width_back');
  const gap = 1.1;
  // the two lengths on the left are labelled at different heights so their labels never overlap
  if (front) parts.push(dimension([0, 0], [0, front.b[1]], [-gap, 0], front.value, labels, 0.36));
  if (length) parts.push(dimension([0, 0], [0, length.b[1]], [-gap * 2.2, 0], length.value, labels, 0.64));
  const [wa, wb] = width ? [width.a[0], width.b[0]] : [0, w];
  parts.push(dimension([wa, bottom], [wb, bottom], [0, -gap], width ? width.value : w, labels));
  if (stageWidth) parts.push(dimension([stageWidth.a[0], l], [stageWidth.b[0], l], [0, gap], stageWidth.value, labels));

  return {
    svg: parts.join(''),
    labels,
    extent: { min: [-gap * 2.2 - 1.2, bottom - gap - 1], max: [w + 1, l + gap + 1] },
  };
}
